import Header from '../components/Header'
import Footer from '../components/Footer'
import {
  Target,
  Eye,
  Shield,
  Users,
  TrendingUp,
  Heart,
  CheckCircle,
  ArrowRight,
  Building2,
  Award,
  Zap,
} from 'lucide-react'
import { Link } from 'react-router-dom'

const AboutPage = () => {
  const stats = [
    { number: '12+', label: 'Active Banks Integrated' },
    { number: '4+', label: 'Active Wallets' },
    { number: '99.9%', label: 'Uptime Guarantee' },
    { number: '24/7', label: 'Customer Support' },
  ]

  const values = [
    {
      icon: <Shield className="h-7 w-7 text-orange-500" />,
      title: 'Security First',
      description:
        'We prioritize the security of every transaction with bank-level encryption, real-time fraud monitoring, and a dedicated security team.',
    },
    {
      icon: <Users className="h-7 w-7 text-orange-500" />,
      title: 'Customer Centric',
      description:
        "Our success is measured by our customers' success. We provide 24/7 dedicated support, onboarding guidance, and ongoing assistance.",
    },
    {
      icon: <TrendingUp className="h-7 w-7 text-orange-500" />,
      title: 'Innovation Driven',
      description:
        'We continuously innovate to bring the latest payment technologies to Pakistani businesses, from QR payments to e-commerce plugins.',
    },
    {
      icon: <Heart className="h-7 w-7 text-orange-500" />,
      title: 'Community Impact',
      description:
        "We believe in empowering local businesses and contributing to Pakistan's digital economy by reducing dependency on cash.",
    },
  ]

  const milestones = [
    { year: '2020', title: 'Company Founded', description: 'MobiPay was established with a mission to digitize payments across Pakistan.' },
    { year: '2021', title: 'Platform Launch', description: 'Launched MobiPay to help businesses accept and manage digital payments.' },
    { year: '2022', title: 'Bank Integrations', description: 'Launched integrations with 12+ major banks including HBL, MCB, UBL, and Meezan Bank.' },
    { year: '2023', title: 'Mobile Payments Launch', description: 'Partnered with UnionPay International to enable QR and Tap & Pay transactions nationwide.' },
    { year: '2024', title: 'E-Commerce Expansion', description: 'Released WooCommerce, Shopify, and Magento plugins, serving hundreds of online merchants.' },
  ]

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* ── Hero ── */}
      <section className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-24 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="container-max section-padding relative">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/20 border border-orange-500/30 rounded-full text-orange-400 text-sm font-medium mb-6">
              About MobiPay
            </div>
            <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
              Driving Pakistan's{' '}
              <span className="text-orange-500">Digital Payment</span>{' '}
              Revolution
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              Our mission is to help Pakistanis avail the benefits of digital payments and drive
              the digital payment landscape forward across the country.
            </p>
            <Link
              to="/get-started"
              className="inline-flex items-center bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-orange-500/30"
            >
              Get Started Today <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── Stats ── */}
      <section className="py-16 bg-orange-500 text-white">
        <div className="container-max section-padding">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-4xl lg:text-5xl font-bold mb-2">{stat.number}</div>
                <div className="text-orange-100 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Our Story ── */}
      <section className="py-24 bg-white">
        <div className="container-max section-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-50 text-orange-600 rounded-full text-sm font-medium">
                Our Story
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
                Built from the ground up for Pakistan
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  Founded with a simple observation — Pakistani businesses needed better, more accessible
                  payment solutions to compete in the digital economy. MobiPay emerged from this gap
                  as a revolutionary digital payment venture aimed at introducing financial technology
                  advancements across all areas of Pakistan.
                </p>
                <p>
                  We encourage cashless sustainability across the nation. As Pakistan moves towards
                  digitization, MobiPay makes it easier to run your business by enabling it to go
                  online and reach untapped markets.
                </p>
                <p>
                  Today, we continue to innovate and expand our services — always keeping our focus
                  on empowering businesses and contributing to Pakistan's digital transformation,
                  from the smallest towns to major cities.
                </p>
              </div>
              <div className="flex flex-wrap gap-4 pt-2">
                {[
                  'Visa Partner',
                  'Fraud Protection',
                  'Self Sign-Up',
                ].map((badge) => (
                  <div key={badge} className="flex items-center gap-2 bg-green-50 text-green-700 border border-green-100 px-4 py-2 rounded-lg text-sm font-medium">
                    <CheckCircle className="h-4 w-4" /> {badge}
                  </div>
                ))}
              </div>
            </div>

            {/* Decorative card stack */}
            <div className="space-y-4">
              <div className="bg-gray-900 rounded-2xl p-6 text-white">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center">
                    <Building2 className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="font-bold">Built for modern commerce</p>
                    <p className="text-gray-400 text-sm">A simpler payments experience</p>
                  </div>
                </div>
                <p className="text-gray-300 text-sm leading-relaxed">
                  MobiPay brings the essential payment tools businesses need into one simple platform.
                </p>
              </div>
              <div className="bg-orange-500 rounded-2xl p-6 text-white">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                    <Award className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="font-bold">Secure by design</p>
                    <p className="text-orange-100 text-sm">Payment protection</p>
                  </div>
                </div>
                <p className="text-orange-100 text-sm leading-relaxed">
                  Use thoughtful security controls and fraud monitoring to help protect every payment.
                </p>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-orange-50 rounded-xl flex items-center justify-center">
                    <Zap className="h-5 w-5 text-orange-500" />
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">Self Sign-Up Portal</p>
                    <p className="text-gray-500 text-sm">Go live in minutes</p>
                  </div>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Paper-less and contact-free onboarding. Register your business from anywhere
                  and start accepting payments the same day.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Mission & Vision ── */}
      <section className="py-24 bg-gray-50">
        <div className="container-max section-padding">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Our Mission &amp; Vision</h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">
              Everything we do is guided by a clear sense of purpose and direction.
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl p-10 border border-gray-100 shadow-sm hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-orange-50 rounded-2xl flex items-center justify-center mb-6">
                <Target className="h-7 w-7 text-orange-500" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Mission</h3>
              <p className="text-gray-600 text-lg leading-relaxed">
                To help Pakistanis avail the benefits of digital payments and drive the digital
                payment landscape in the country — making secure, affordable, and easy-to-use
                payment solutions accessible to businesses of all sizes.
              </p>
            </div>
            <div className="bg-gray-900 rounded-2xl p-10 text-white hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-orange-500/20 rounded-2xl flex items-center justify-center mb-6">
                <Eye className="h-7 w-7 text-orange-400" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Our Vision</h3>
              <p className="text-gray-300 text-lg leading-relaxed">
                To become the backbone of Pakistan's digital payment ecosystem — enabling seamless
                transactions, fostering economic growth, and reducing dependency on cash across
                all sectors of the economy.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Milestones ── */}
      <section className="py-24 bg-white">
        <div className="container-max section-padding">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-50 text-orange-600 rounded-full text-sm font-medium mb-4">
              Our Journey
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Key Milestones</h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">
              A look at how MobiPay has grown since its founding.
            </p>
          </div>
          <div className="relative">
            {/* vertical line */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-200 hidden md:block" />
            <div className="space-y-8">
              {milestones.map((m, i) => (
                <div key={i} className="relative flex items-start gap-6 md:gap-10">
                  {/* dot */}
                  <div className="flex-shrink-0 w-16 h-16 bg-orange-500 rounded-2xl flex items-center justify-center shadow-md shadow-orange-200 z-10">
                    <span className="text-white font-bold text-xs text-center leading-tight">{m.year}</span>
                  </div>
                  <div className="bg-gray-50 border border-gray-100 rounded-2xl p-6 flex-1 hover:border-orange-200 hover:shadow-md transition-all duration-200">
                    <h3 className="text-lg font-bold text-gray-900 mb-1">{m.title}</h3>
                    <p className="text-gray-500 leading-relaxed">{m.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Values ── */}
      <section className="py-24 bg-gray-50">
        <div className="container-max section-padding">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-50 text-orange-600 rounded-full text-sm font-medium mb-4">
              Our Values
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">What we stand for</h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">
              These core values guide everything we do and shape how we serve our customers.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, i) => (
              <div key={i} className="bg-white rounded-2xl p-8 text-center shadow-sm border border-gray-100 hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                <div className="inline-flex items-center justify-center w-14 h-14 bg-orange-50 rounded-2xl mb-5">
                  {value.icon}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">{value.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Why Choose MobiPay ── */}
      <section className="py-24 bg-white">
        <div className="container-max section-padding">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Why Choose MobiPay?</h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">
              Built from the ground up to serve Pakistani businesses with reliable, modern payment infrastructure.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: <Shield className="h-8 w-8 text-orange-500" />,
                title: 'Bank-Level Security',
                description: 'End-to-end encryption, our Guardian fraud monitoring system, and industry-grade infrastructure protect every transaction.',
              },
              {
                icon: <Zap className="h-8 w-8 text-orange-500" />,
                title: 'Self Sign-Up in Minutes',
                description: 'Paper-less, contact-free digital onboarding. Register your business and go live without visiting any office.',
              },
              {
                icon: <Users className="h-8 w-8 text-orange-500" />,
                title: '24/7 Dedicated Support',
                description: 'Our local support team is available around the clock via email, chat, and phone to assist you every step of the way.',
              },
            ].map((item, i) => (
              <div key={i} className="bg-gray-50 rounded-2xl p-8 border border-gray-100 hover:shadow-lg transition-all duration-300 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-50 rounded-2xl mb-6">
                  {item.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-500 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-20 bg-orange-500 text-white">
        <div className="container-max section-padding text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-4">Ready to join MobiPay?</h2>
          <p className="text-orange-100 text-lg mb-8 max-w-xl mx-auto">
            We have a seamless self sign-up process so you can become a partner in a matter of minutes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/get-started"
              className="inline-flex items-center justify-center bg-white text-orange-600 hover:bg-orange-50 px-8 py-4 rounded-lg font-bold transition-all duration-200 shadow-md"
            >
              Get Started <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center border-2 border-white/40 hover:border-white text-white px-8 py-4 rounded-lg font-semibold transition-all duration-200"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default AboutPage
